import { memo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

interface IPostWriterSubmenuProps {
  onGenerateTopics?: () => void;
  showGenerateButton?: boolean;
}

const PostWriterSubmenu = ({ onGenerateTopics, showGenerateButton = false }: IPostWriterSubmenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  // Check if a path is active
  const isActivePath = (path: string) => location.pathname === path;

  // Base button styles
  const baseButtonStyles = 'w-full text-left px-2 py-2 rounded-md text-xs font-medium transition-colors';
  const activeStyles = 'bg-sidebar-primary text-sidebar-primary-foreground';
  const inactiveStyles = 'text-foreground-muted hover:text-foreground hover:bg-sidebar-accent';
  const sectionHeaderStyles = 'w-full text-left px-2 py-1.5 text-xs font-semibold text-foreground-muted uppercase tracking-wide cursor-default';
  const subItemStyles = 'pl-4'; // Indentation for sub-items

  return (
    <div className="h-full bg-sidebar border-r border-sidebar-border">
      <div className="p-2">
        <h2 className="text-xs font-semibold text-foreground-muted uppercase tracking-wide mb-2 px-2">
          Creator
        </h2>
        <nav className="space-y-0.5">
          {/* Topics - clickable */}
          <button
            onClick={() => handleNavigate('/post-writer')}
            className={`${baseButtonStyles} ${
              isActivePath('/post-writer') ? activeStyles : inactiveStyles
            }`}
          >
            Topics
          </button>

          {/* Posts Section Header - NOT clickable */}
          <div className={`${sectionHeaderStyles} mt-3`}>
            Posts
          </div>

          {/* Posts > Draft */}
          <button
            onClick={() => handleNavigate('/posts/draft')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              isActivePath('/posts/draft') ? activeStyles : inactiveStyles
            }`}
          >
            Draft
          </button>

          {/* Posts > Ready */}
          <button
            onClick={() => handleNavigate('/posts/ready')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              isActivePath('/posts/ready') ? activeStyles : inactiveStyles
            }`}
          >
            Ready
          </button>

          {/* Reels Section Header - NOT clickable */}
          <div className={`${sectionHeaderStyles} mt-3`}>
            Reels
          </div>

          {/* Reels > Draft */}
          <button
            onClick={() => handleNavigate('/reels/draft')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              isActivePath('/reels/draft') ? activeStyles : inactiveStyles
            }`}
          >
            Draft
          </button>

          {/* Reels > Ready */}
          <button
            onClick={() => handleNavigate('/reels/ready')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              isActivePath('/reels/ready') ? activeStyles : inactiveStyles
            }`}
          >
            Ready
          </button>

          {/* Divider */}
          <div className="h-px bg-sidebar-border my-3" />

          {/* Writing Styles */}
          <button
            onClick={() => handleNavigate('/writing-styles')}
            className={`${baseButtonStyles} ${
              isActivePath('/writing-styles') ? activeStyles : inactiveStyles
            }`}
          >
            Writing Styles
          </button>

          {/* AI Avatar Section Header */}
          <div className={`${sectionHeaderStyles} mt-3`}>
            AI Avatar
          </div>

          {/* Reference Persons */}
          <button
            onClick={() => handleNavigate('/ai-avatar/reference-persons')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              isActivePath('/ai-avatar/reference-persons') ? activeStyles : inactiveStyles
            }`}
          >
            Reference Persons
          </button>

          {/* My Avatars */}
          <button
            onClick={() => handleNavigate('/ai-avatar/my-avatars')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              location.pathname.startsWith('/ai-avatar/my-avatars') ? activeStyles : inactiveStyles
            }`}
          >
            My Avatars
          </button>

          {/* Photostudio Section Header */}
          <div className={`${sectionHeaderStyles} mt-3`}>
            Photostudio
          </div>

          {/* Photostudio > Generate */}
          <button
            onClick={() => handleNavigate('/photostudio')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              isActivePath('/photostudio') ? activeStyles : inactiveStyles
            }`}
          >
            Generate
          </button>

          {/* Photostudio > Projects */}
          <button
            onClick={() => handleNavigate('/photostudio/projects')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              location.pathname.startsWith('/photostudio/projects') ? activeStyles : inactiveStyles
            }`}
          >
            Projects
          </button>

          {/* Photostudio > Elements */}
          <button
            onClick={() => handleNavigate('/photostudio/elements')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              isActivePath('/photostudio/elements') ? activeStyles : inactiveStyles
            }`}
          >
            Elements
          </button>

          {/* Voices Section Header */}
          <div className={`${sectionHeaderStyles} mt-3`}>
            Voices
          </div>

          {/* Clone Voice */}
          <button
            onClick={() => handleNavigate('/voices/clone')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              isActivePath('/voices/clone') ? activeStyles : inactiveStyles
            }`}
          >
            Clone Voice
          </button>

          {/* My Voices */}
          <button
            onClick={() => handleNavigate('/voices/my-voices')}
            className={`${baseButtonStyles} ${subItemStyles} ${
              isActivePath('/voices/my-voices') ? activeStyles : inactiveStyles
            }`}
          >
            My Voices
          </button>

          {/* Divider */}
          <div className="h-px bg-sidebar-border my-3" />

          {/* Calendar */}
          <button
            onClick={() => handleNavigate('/scheduled')}
            className={`${baseButtonStyles} ${
              isActivePath('/scheduled') ? activeStyles : inactiveStyles
            }`}
          >
            Calendar
          </button>

          {/* Logs */}
          <button
            onClick={() => handleNavigate('/scheduled-posts')}
            className={`${baseButtonStyles} ${
              isActivePath('/scheduled-posts') ? activeStyles : inactiveStyles
            }`}
          >
            Logs
          </button>
        </nav>

        {showGenerateButton && onGenerateTopics && (
          <div className="mt-4">
            <Button onClick={onGenerateTopics} className="w-full" size="sm">
              <Sparkles className="h-3 w-3 mr-1" />
              <span className="text-xs">Generate</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const PostWriterSubmenuMemo = memo(PostWriterSubmenu);
